/**
 * Duration helpers.
 *
 * Elapsed time is always DERIVED from stored timestamps, never accumulated by a
 * ticking counter, so a locked screen, a backgrounded tab or a suspended PWA
 * cannot make the timer drift.
 */

export const MS_PER_SECOND = 1000;

/** Milliseconds between start and (end ?? now). Never negative. */
export function elapsedMs(
  startTimestamp: number | undefined,
  endTimestamp?: number,
  now: number = Date.now(),
): number {
  if (!startTimestamp) return 0;
  const end = endTimestamp ?? now;
  return Math.max(0, end - startTimestamp);
}

export function elapsedSeconds(
  startTimestamp: number | undefined,
  endTimestamp?: number,
  now: number = Date.now(),
): number {
  return Math.floor(elapsedMs(startTimestamp, endTimestamp, now) / MS_PER_SECOND);
}

/** "HH:MM:SS" — hours are not capped at 24. */
export function formatDuration(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  return [hours, minutes, seconds].map((n) => n.toString().padStart(2, '0')).join(':');
}

/** "1h 04m" style, for compact history rows. */
export function formatDurationShort(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
}
